// competition-analytics.js — Tab 4: Analytics (F.4.2 + F.4.3).
// Sub-tabs: SEASON ARC · GAP TRACKER · FINGERPRINT · FORECAST
// 1980s phosphor-console aesthetic throughout.

import { apiFetch } from './utils.js';
import {
  makeSeasonArcChart, makeGapChart, makeRadarChart,
  destroyChart, RETRO, captionColor, seasonColor
} from './competition-charts.js';

// ── Retro CSS (injected once) ─────────────────────────────────────────────────
let _cssInjected = false;
function injectRetroCSS() {
  if (_cssInjected) return;
  _cssInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .retro-console {
      font-family: 'Courier New', Courier, monospace;
      background: #050a05;
      color: #33ff33;
      border: 1px solid #33ff33;
      box-shadow: 0 0 24px rgba(51,255,51,0.18), inset 0 0 40px rgba(0,0,0,0.6);
      border-radius: 4px;
      position: relative;
      overflow: hidden;
    }
    .retro-console::before {
      content: '';
      position: absolute;
      inset: 0;
      background: repeating-linear-gradient(
        to bottom, transparent 0px, transparent 3px,
        rgba(0,0,0,0.08) 3px, rgba(0,0,0,0.08) 4px
      );
      pointer-events: none;
      z-index: 1;
    }
    .retro-header {
      background: #33ff33; color: #050a05; font-weight: 700;
      font-size: .8rem; letter-spacing: .12em;
      padding: .4rem .9rem; display: flex;
      justify-content: space-between; align-items: center;
    }
    .retro-header-rhs { font-size: .7rem; opacity: .7; }
    .retro-body { padding: 1rem 1.1rem; position: relative; z-index: 2; }
    .retro-prompt { font-size: .82rem; color: #1a8c1a; margin-bottom: .35rem; letter-spacing: .04em; }
    .retro-prompt::before { content: '> '; color: #33ff33; }
    .retro-select {
      background: #050a05; color: #33ff33; border: 1px solid #33ff33;
      font-family: 'Courier New', Courier, monospace; font-size: .82rem;
      padding: .3rem .6rem; outline: none; border-radius: 0; cursor: pointer; min-width: 200px;
    }
    .retro-select:focus { box-shadow: 0 0 8px rgba(51,255,51,0.5); }
    .retro-select option { background: #050a05; color: #33ff33; }
    .retro-input {
      background: #050a05; color: #33ff33; border: 1px solid #33ff33;
      font-family: 'Courier New', Courier, monospace; font-size: .82rem;
      padding: .3rem .6rem; outline: none; border-radius: 0; width: 240px;
    }
    .retro-input:focus { box-shadow: 0 0 8px rgba(51,255,51,0.5); }
    .retro-btn {
      background: transparent; color: #33ff33; border: 1px solid #33ff33;
      font-family: 'Courier New', Courier, monospace; font-size: .78rem;
      padding: .3rem .8rem; cursor: pointer; letter-spacing: .08em;
      transition: background .1s, color .1s;
    }
    .retro-btn:hover { background: #33ff33; color: #050a05; }
    .retro-btn.active { background: #33ff33; color: #050a05; font-weight: 700; }
    .retro-btn:disabled { opacity: .4; cursor: not-allowed; }
    .retro-btn:disabled:hover { background: transparent; color: #33ff33; }
    .retro-tab-bar { display: flex; gap: 0; border-bottom: 1px solid #33ff33; margin-bottom: 1rem; }
    .retro-tab-bar .retro-btn { border-bottom: none; border-right: none; }
    .retro-tab-bar .retro-btn:last-child { border-right: 1px solid #33ff33; }
    .retro-divider { border: none; border-top: 1px solid #1a8c1a; margin: .75rem 0; }
    .retro-chip {
      display: inline-block; border: 1px solid currentColor;
      padding: .1rem .4rem; font-size: .72rem; margin: .15rem .2rem .15rem 0;
      letter-spacing: .04em; cursor: pointer;
    }
    .retro-chip.active { background: currentColor; }
    .retro-chip.active span { color: #050a05; }
    .retro-glow  { text-shadow: 0 0 8px rgba(51,255,51,0.7); }
    .retro-warn  { color: #ffb000; text-shadow: 0 0 6px rgba(255,176,0,0.6); }
    .retro-err   { color: #ff4444; }
    .retro-dim   { color: #1a8c1a; }
    .retro-canvas-wrap {
      position: relative; height: 280px; margin: .75rem 0;
      background: #020602; border: 1px solid #1a8c1a; padding: .5rem;
    }
    .retro-canvas-wrap.radar { height: 300px; }
    .retro-stat-row { display: flex; gap: 1.5rem; flex-wrap: wrap; margin: .5rem 0; font-size: .78rem; }
    .retro-stat { display: flex; flex-direction: column; gap: .1rem; }
    .retro-stat-label { color: #1a8c1a; font-size: .7rem; letter-spacing: .06em; }
    .retro-stat-val   { color: #33ff33; font-size: .9rem; letter-spacing: .04em; }
    .retro-record-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px,1fr)); gap: .5rem; margin-top: .5rem; }
    .retro-record-card { border: 1px solid #1a8c1a; padding: .5rem .75rem; font-size: .78rem; }
    .retro-wl { display: flex; gap: .5rem; margin-top: .25rem; }
    .retro-wl-w { color: #33ff33; } .retro-wl-l { color: #ff4444; } .retro-wl-t { color: #ffb000; }
    .retro-splash {
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      min-height: 260px; gap: 1rem; font-size: .85rem;
    }
    .retro-splash-title { font-size: 1rem; letter-spacing: .15em; text-shadow: 0 0 12px rgba(51,255,51,0.8); }
    .retro-blink { animation: retro-blink 1.1s step-end infinite; }
    @keyframes retro-blink { 0%,100% { opacity:1 } 50% { opacity:0 } }
    .retro-loading { display: flex; align-items: center; gap: .5rem; font-size: .82rem; color: #1a8c1a; padding: 1rem 0; }
    .retro-vt { width:100%; border-collapse:collapse; font-size:.78rem; margin-top:.5rem; }
    .retro-vt th { color:#1a8c1a; text-align:left; padding:.2rem .4rem; border-bottom:1px solid #1a8c1a; font-weight:normal; letter-spacing:.06em; }
    .retro-vt td { padding:.2rem .4rem; color:#33ff33; border-bottom:1px solid rgba(51,255,51,0.08); }
    .retro-book-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(140px,1fr)); gap:.4rem; margin:.5rem 0; }
    .retro-book-cell { border:1px solid #1a8c1a; padding:.4rem .6rem; font-size:.76rem; }
    .retro-book-cell .bc-name { color:#1a8c1a; font-size:.68rem; letter-spacing:.06em; margin-bottom:.2rem; }
    .retro-book-cell .bc-scores { display:flex; gap:.4rem; margin-bottom:.2rem; }
    .retro-forecast-card {
      border: 1px solid #33ff33; padding: 1rem 1.25rem; margin-bottom: .75rem;
      background: rgba(51,255,51,0.03);
    }
    .retro-progress-bar {
      height: 6px; background: #1a2c1a; border: 1px solid #1a8c1a; margin: .4rem 0;
    }
    .retro-progress-fill { height: 100%; background: #33ff33; transition: width .3s; }
    .retro-timeline { margin-top:.5rem; }
    .retro-timeline-row {
      display:flex; gap:.75rem; align-items:center; padding:.3rem 0;
      border-bottom:1px solid rgba(51,255,51,0.08); font-size:.78rem;
    }
    .retro-timeline-row .tl-show { color:#1a8c1a; min-width:48px; }
    .retro-timeline-row .tl-range { font-variant-numeric:tabular-nums; }
    .retro-timeline-row .tl-conf { font-size:.7rem; color:#1a8c1a; }
    .retro-timeline-row .tl-delta { font-size:.75rem; }
    .retro-narrative-box {
      background:#020602; border:1px solid #1a8c1a; padding:.75rem 1rem;
      font-size:.82rem; color:#aaffaa; line-height:1.55; margin-top:.5rem;
      white-space:pre-wrap;
    }
    .retro-field-table { width:100%; border-collapse:collapse; font-size:.78rem; margin-top:.5rem; }
    .retro-field-table th { color:#1a8c1a; text-align:left; padding:.2rem .4rem; border-bottom:1px solid #1a8c1a; font-weight:normal; }
    .retro-field-table td { padding:.2rem .4rem; color:#33ff33; border-bottom:1px solid rgba(51,255,51,0.06); }
    .retro-field-table tr.is-primary td { color:#33ff33; font-weight:700; }
  `;
  document.head.appendChild(style);
}

// ── Module state ──────────────────────────────────────────────────────────────
let _activeTab  = 'arc';

// Arc state
let _arcChart   = null;
let _arcBandId  = null;
let _arcSeasons = [];
let _arcVisible = new Set();

// Gap state
let _gapChart   = null;
let _gapBandId  = null;
let _gapCompIds = [];
let _gapSeasons = [];

// Fingerprint state
let _fpChart    = null;
let _fpBandId   = null;
let _fpSeasons  = [];

// Forecast state
let _fcBandId   = null;
let _fcSeason   = new Date().getFullYear();

// ── Main entry ────────────────────────────────────────────────────────────────
export async function renderAnalyticsTab() {
  injectRetroCSS();
  return `
    <div class="retro-console">
      <div class="retro-header">
        <span>WMM COMPETITION ANALYTICS  v2.4 █</span>
        <span class="retro-header-rhs">SYS:OK &nbsp; DB:LIVE &nbsp; <span class="retro-blink">▮</span></span>
      </div>
      <div class="retro-body">
        <div class="retro-tab-bar">
          <button class="retro-btn active" id="anal-tab-arc"         onclick="window._analTab('arc')">        [SEASON ARC]</button>
          <button class="retro-btn"        id="anal-tab-gap"         onclick="window._analTab('gap')">         GAP TRACKER </button>
          <button class="retro-btn"        id="anal-tab-fingerprint" onclick="window._analTab('fingerprint')"> FINGERPRINT </button>
          <button class="retro-btn"        id="anal-tab-forecast"    onclick="window._analTab('forecast')">    FORECAST    </button>
        </div>
        <div id="anal-tab-content"></div>
      </div>
    </div>
  `;
}

export async function initAnalyticsTab() {
  _activeTab = 'arc';
  window._analTab = async function(tab) {
    _activeTab = tab;
    document.querySelectorAll('.retro-tab-bar .retro-btn').forEach(b => b.classList.remove('active'));
    document.getElementById(`anal-tab-${tab}`)?.classList.add('active');
    const fns = { arc: renderArcTab, gap: renderGapTab, fingerprint: renderFingerprintTab, forecast: renderForecastTab };
    await fns[tab]?.();
  };
  await renderArcTab();
}

// ── Shared helpers ────────────────────────────────────────────────────────────
async function populateBandSel(selId, currentVal) {
  const sel = document.getElementById(selId);
  if (!sel) return;
  try {
    const bands = window._compBandsCache || await apiFetch('/api/competition-analysis/bands?limit=500');
    window._compBandsCache = bands;
    sel.innerHTML = `<option value="">-- PICK A BAND --</option>` +
      bands.map(b => `<option value="${b.id}" ${b.id == currentVal ? 'selected' : ''}>${b.name}${b.state ? ' ('+b.state+')' : ''}${b.client_id ? ' ◆' : ''}</option>`).join('');
  } catch { sel.innerHTML = `<option value="">-- ERR: CANNOT LOAD --</option>`; }
}

function seasonChipsHtml(selectedYears, handler) {
  const cy = new Date().getFullYear();
  return [cy, cy-1, cy-2].map(y => {
    const on = selectedYears.includes(y);
    return `<span class="retro-chip ${on?'active':''}" style="color:${on?'#33ff33':'#1a8c1a'}"
      onclick="${handler}(${y})"><span>${y}</span></span>`;
  }).join('');
}

// ── TAB A — Season Arc ────────────────────────────────────────────────────────
async function renderArcTab() {
  const el = document.getElementById('anal-tab-content');
  if (!el) return;
  if (_arcChart) { destroyChart(document.getElementById('arc-canvas')); _arcChart = null; }
  if (!_arcSeasons.length) _arcSeasons = [new Date().getFullYear()];

  el.innerHTML = `
    <div class="retro-prompt">CONFIGURE SEASON ARC PARAMETERS</div>
    <div style="display:flex;flex-wrap:wrap;gap:.75rem;align-items:flex-end;margin-bottom:.75rem">
      <div>
        <div class="retro-dim" style="font-size:.7rem;margin-bottom:.25rem;letter-spacing:.06em">BAND DESIGNATION</div>
        <select class="retro-select" id="arc-band-sel" onchange="window._arcBandChange()">
          <option value="">-- PICK A BAND --</option>
        </select>
      </div>
      <div>
        <div class="retro-dim" style="font-size:.7rem;margin-bottom:.25rem;letter-spacing:.06em">SEASON(S)</div>
        <div id="arc-season-chips">${seasonChipsHtml(_arcSeasons, 'window._arcSeasonToggle')}</div>
      </div>
    </div>
    <div id="arc-caption-toggles" style="display:none;margin-bottom:.75rem">
      <div class="retro-dim" style="font-size:.7rem;margin-bottom:.25rem;letter-spacing:.06em">CAPTION OVERLAY</div>
      <div id="arc-cap-chips"></div>
    </div>
    <div id="arc-chart-area">
      <div class="retro-splash">
        <div class="retro-splash-title retro-glow">SEASON ARC MODULE</div>
        <div class="retro-dim">MULTI-SEASON TRAJECTORY · CAPTION OVERLAYS · FORECAST EXTENSION</div>
        <div style="margin-top:.5rem">SELECT BAND TO INITIALIZE <span class="retro-blink">_</span></div>
      </div>
    </div>
  `;
  await populateBandSel('arc-band-sel', _arcBandId);
  if (_arcBandId) { document.getElementById('arc-band-sel').value = _arcBandId; await loadArcData(); }

  window._arcBandChange = async () => { _arcBandId = parseInt(document.getElementById('arc-band-sel')?.value)||null; if (_arcBandId) await loadArcData(); };
  window._arcSeasonToggle = y => {
    if (_arcSeasons.includes(y)) { if (_arcSeasons.length===1) return; _arcSeasons=_arcSeasons.filter(s=>s!==y); }
    else _arcSeasons=[..._arcSeasons,y].sort();
    document.getElementById('arc-season-chips').innerHTML = seasonChipsHtml(_arcSeasons,'window._arcSeasonToggle');
    if (_arcBandId) loadArcData();
  };
  window._arcCaptionToggle = g => {
    if (_arcVisible.has(g)) _arcVisible.delete(g); else _arcVisible.add(g);
    buildArcCapChips(window._arcData?.captionGroups||[]);
    if (window._arcData) redrawArcChart(window._arcData);
  };
}

async function loadArcData() {
  const area = document.getElementById('arc-chart-area');
  if (!area||!_arcBandId) return;
  area.innerHTML = `<div class="retro-loading"><span class="retro-blink">█</span> LOADING ARC DATA...</div>`;
  try {
    const data = await apiFetch(`/api/competition-analysis/season-arc/${_arcBandId}?seasons=${_arcSeasons.join(',')}`);
    window._arcData = data;
    const capGroups = new Set();
    for (const s of data.seasons||[]) for (const e of s.entries||[]) Object.keys(e.captions||{}).forEach(g=>capGroups.add(g));
    data.captionGroups = [...capGroups].sort();
    buildArcCapChips(data.captionGroups);
    renderArcChartArea(data);
  } catch (err) { area.innerHTML=`<div class="retro-err" style="padding:1rem">ERR: ${err.message}</div>`; }
}

function buildArcCapChips(groups) {
  const el=document.getElementById('arc-cap-chips'), wrap=document.getElementById('arc-caption-toggles');
  if (!el||!groups.length) { if(wrap) wrap.style.display='none'; return; }
  wrap.style.display='';
  el.innerHTML=groups.map(g=>{const on=_arcVisible.has(g),col=captionColor(g);return`<span class="retro-chip ${on?'active':''}" style="color:${col};border-color:${col}" onclick="window._arcCaptionToggle('${g}')"><span>${g.toUpperCase()}</span></span>`;}).join('');
}

function renderArcChartArea(data) {
  const area=document.getElementById('arc-chart-area'); if(!area) return;
  const {seasons,anomalies}=data;
  if(!seasons?.length||!seasons[0].entries.length) { area.innerHTML=`<div class="retro-dim" style="padding:1rem">NO DATA FOR SELECTED SEASONS.</div>`; return; }
  const flags=(anomalies?.flags||[]);
  const flagHtml=flags.length?`<div style="margin-bottom:.6rem;display:flex;gap:.4rem;flex-wrap:wrap">${flags.map(f=>`<span class="retro-chip retro-warn" style="border-color:#ffb000;cursor:default">⚠ ${f.type.replace('_',' ')} SHW${f.show}</span>`).join('')}</div>`:'';
  const ls=seasons[seasons.length-1];
  const velocityRows=ls.entries.slice(1).map((e,i)=>{const prev=ls.entries[i],d=(e.total_score-prev.total_score).toFixed(3),col=parseFloat(d)>=0?'#33ff33':'#ff4444';return`<tr><td>SHW ${e.show_number}</td><td>${e.total_score.toFixed(3)}</td><td style="color:${col}">${parseFloat(d)>=0?'+':''}${d}</td><td>${(e.competition_name||'').slice(0,28)}</td></tr>`;}).join('');
  const fc=ls.forecast;
  const fcHtml=fc?`<div class="retro-stat-row" style="margin-top:.5rem"><div class="retro-stat"><div class="retro-stat-label">CONF</div><div class="retro-stat-val">${fc.confidence}</div></div><div class="retro-stat"><div class="retro-stat-label">PROJ LOW</div><div class="retro-stat-val">${fc.projected_low}</div></div><div class="retro-stat"><div class="retro-stat-label">PROJ MID</div><div class="retro-stat-val retro-glow">${fc.projected_mid}</div></div><div class="retro-stat"><div class="retro-stat-label">PROJ HIGH</div><div class="retro-stat-val">${fc.projected_high}</div></div>${fc.cold_start?'<div class="retro-stat"><div class="retro-stat-label"></div><div class="retro-stat-val retro-warn">COLD START</div></div>':''}</div>`:'';
  area.innerHTML=`${flagHtml}<div class="retro-canvas-wrap"><canvas id="arc-canvas"></canvas></div>${fcHtml}<hr class="retro-divider"><div class="retro-dim" style="font-size:.7rem;letter-spacing:.06em;margin-bottom:.3rem">VELOCITY LOG — ${ls.season}</div>${velocityRows?`<table class="retro-vt"><thead><tr><th>SHOW</th><th>SCORE</th><th>Δ</th><th>EVENT</th></tr></thead><tbody>${velocityRows}</tbody></table>`:'<div class="retro-dim" style="font-size:.78rem">INSUFFICIENT DATA</div>'}`;
  requestAnimationFrame(()=>{const c=document.getElementById('arc-canvas');if(!c)return;destroyChart(c);_arcChart=makeSeasonArcChart(c,seasons,_arcVisible);});
}

function redrawArcChart(data) {
  if(_arcChart){destroyChart(document.getElementById('arc-canvas'));_arcChart=null;}
  requestAnimationFrame(()=>{const c=document.getElementById('arc-canvas');if(!c)return;_arcChart=makeSeasonArcChart(c,data.seasons,_arcVisible);});
}

// ── TAB B — Gap Tracker ───────────────────────────────────────────────────────
async function renderGapTab() {
  const el=document.getElementById('anal-tab-content'); if(!el) return;
  if(_gapChart){destroyChart(document.getElementById('gap-canvas'));_gapChart=null;}
  if(!_gapSeasons.length) _gapSeasons=[new Date().getFullYear()];

  el.innerHTML=`
    <div class="retro-prompt">CONFIGURE GAP TRACKER PARAMETERS</div>
    <div style="display:flex;flex-wrap:wrap;gap:.75rem;align-items:flex-end;margin-bottom:.75rem">
      <div>
        <div class="retro-dim" style="font-size:.7rem;margin-bottom:.25rem;letter-spacing:.06em">PRIMARY BAND</div>
        <select class="retro-select" id="gap-primary-sel" onchange="window._gapPrimaryChange()"><option value="">-- PICK A BAND --</option></select>
      </div>
      <div>
        <div class="retro-dim" style="font-size:.7rem;margin-bottom:.25rem;letter-spacing:.06em">SEASON</div>
        <select class="retro-select" id="gap-season-sel" onchange="window._gapLoad()">
          ${[new Date().getFullYear(),new Date().getFullYear()-1,new Date().getFullYear()-2].map(y=>`<option value="${y}" ${y===(_gapSeasons[0]||new Date().getFullYear())?'selected':''}>${y}</option>`).join('')}
        </select>
      </div>
    </div>
    <div id="gap-comp-section" style="display:${_gapBandId?'':'none'};margin-bottom:.75rem">
      <div class="retro-dim" style="font-size:.7rem;margin-bottom:.25rem;letter-spacing:.06em">COMPETITORS (MAX 3)</div>
      <div style="display:flex;gap:.5rem;align-items:center;flex-wrap:wrap">
        <select class="retro-select" id="gap-comp-sel" style="min-width:220px"><option value="">-- SELECT COMPETITOR --</option></select>
        <button class="retro-btn" onclick="window._gapAddComp()">[ + ADD ]</button>
      </div>
      <div id="gap-comp-tags" style="display:flex;gap:.4rem;flex-wrap:wrap;margin-top:.4rem"></div>
    </div>
    <div id="gap-chart-area">
      <div class="retro-splash">
        <div class="retro-splash-title retro-glow">GAP TRACKER MODULE</div>
        <div class="retro-dim">POSITIVE GAP = PRIMARY BAND IS AHEAD</div>
        <div style="margin-top:.5rem">SELECT PRIMARY BAND TO INITIALIZE <span class="retro-blink">_</span></div>
      </div>
    </div>
  `;
  await Promise.all([populateBandSel('gap-primary-sel',_gapBandId),populateBandSel('gap-comp-sel',null)]);
  if(_gapBandId){document.getElementById('gap-primary-sel').value=_gapBandId;document.getElementById('gap-comp-section').style.display='';renderGapCompTags();if(_gapCompIds.length)await loadGapData();}

  window._gapPrimaryChange=async()=>{_gapBandId=parseInt(document.getElementById('gap-primary-sel')?.value)||null;_gapCompIds=[];document.getElementById('gap-comp-section').style.display=_gapBandId?'':'none';renderGapCompTags();clearGapChart();};
  window._gapAddComp=async()=>{const sel=document.getElementById('gap-comp-sel'),id=parseInt(sel?.value);if(!id||_gapCompIds.includes(id)||id===_gapBandId)return;if(_gapCompIds.length>=3){alert('MAX 3 COMPETITORS');return;}_gapCompIds.push(id);renderGapCompTags();if(_gapBandId&&_gapCompIds.length)await loadGapData();};
  window._gapRemoveComp=id=>{_gapCompIds=_gapCompIds.filter(c=>c!==id);renderGapCompTags();if(_gapCompIds.length)loadGapData();else clearGapChart();};
  window._gapLoad=async()=>{const y=parseInt(document.getElementById('gap-season-sel')?.value);if(y)_gapSeasons=[y];if(_gapBandId&&_gapCompIds.length)await loadGapData();};
}

function renderGapCompTags(){const el=document.getElementById('gap-comp-tags');if(!el)return;const bands=window._compBandsCache||[];el.innerHTML=_gapCompIds.map(id=>{const b=bands.find(b=>b.id===id);return`<span class="retro-chip active" style="color:#33ff33;cursor:default"><span>${b?.name||'ID:'+id}</span><button style="background:none;border:none;color:#33ff33;cursor:pointer;font-family:monospace;margin-left:.3rem;padding:0" onclick="window._gapRemoveComp(${id})">×</button></span>`;}).join('');}

function clearGapChart(){const area=document.getElementById('gap-chart-area');if(!area)return;if(_gapChart){destroyChart(document.getElementById('gap-canvas'));_gapChart=null;}area.innerHTML=`<div class="retro-dim" style="padding:.75rem;font-size:.8rem">SELECT COMPETITORS TO PLOT GAP DATA.</div>`;}

async function loadGapData(){const area=document.getElementById('gap-chart-area');if(!area||!_gapBandId||!_gapCompIds.length)return;area.innerHTML=`<div class="retro-loading"><span class="retro-blink">█</span> LOADING GAP DATA...</div>`;try{const p=new URLSearchParams({competitor_ids:_gapCompIds.join(','),seasons:(_gapSeasons.length?_gapSeasons:[new Date().getFullYear()]).join(',')});const data=await apiFetch(`/api/competition-analysis/gap-tracker/${_gapBandId}?${p}`);renderGapChartArea(data);}catch(err){area.innerHTML=`<div class="retro-err" style="padding:1rem">ERR: ${err.message}</div>`;}}

function renderGapChartArea(data){const area=document.getElementById('gap-chart-area');if(!area)return;const{primary,competitors}=data;if(!competitors.some(c=>c.shared_events?.length)){area.innerHTML=`<div class="retro-dim" style="padding:1rem">NO SHARED EVENTS FOUND.</div>`;return;}
  const recordCards=competitors.map((comp,i)=>{const col=['#33ff33','#ffb000','#00ffff'][i%3],{wins,losses,ties}=comp.record,pct=wins+losses+ties>0?Math.round((wins/(wins+losses+ties))*100):0;return`<div class="retro-record-card" style="border-color:${col}"><div style="color:${col};font-size:.78rem;font-weight:700;letter-spacing:.06em">${comp.band?.name||'—'}</div><div class="retro-wl"><span class="retro-wl-w">W:${wins}</span><span class="retro-wl-l">L:${losses}</span><span class="retro-wl-t">T:${ties}</span><span style="color:${col}">[ ${pct}% ]</span></div>${renderCaptionWL(comp.caption_record)}</div>`;}).join('');
  const latestGaps=competitors.map((comp,i)=>{const ev=comp.shared_events?.slice(-1)[0];if(!ev)return'';const col=['#33ff33','#ffb000','#00ffff'][i%3],ahead=ev.gap>=0;return`<div class="retro-stat"><div class="retro-stat-label" style="color:${col}">${comp.band?.name?.slice(0,16)||'—'}</div><div class="retro-stat-val" style="color:${ahead?'#33ff33':'#ff4444'}">${ev.gap>=0?'+':''}${ev.gap.toFixed(3)} · ${Math.abs(ev.gap)<=3?'ANY':Math.abs(ev.gap)<=6?'CLOSE':'CLEAR'}</div></div>`;}).join('');
  area.innerHTML=`<div class="retro-canvas-wrap"><canvas id="gap-canvas"></canvas></div><hr class="retro-divider"><div class="retro-dim" style="font-size:.7rem;letter-spacing:.06em;margin-bottom:.4rem">LATEST GAP</div><div class="retro-stat-row">${latestGaps}</div><hr class="retro-divider"><div class="retro-dim" style="font-size:.7rem;letter-spacing:.06em;margin-bottom:.4rem">H2H RECORD  vs  ${primary?.name||'PRIMARY'}</div><div class="retro-record-grid">${recordCards}</div>`;
  requestAnimationFrame(()=>{const c=document.getElementById('gap-canvas');if(!c)return;destroyChart(c);_gapChart=makeGapChart(c,primary,competitors);});}

function renderCaptionWL(record){if(!record||!Object.keys(record).length)return'';return`<div style="margin-top:.35rem;font-size:.7rem;color:#1a8c1a">${Object.entries(record).map(([g,r])=>`${g.toUpperCase().slice(0,4)}: <span style="color:#33ff33">${r.wins}W</span>-<span style="color:#ff4444">${r.losses}L</span>`).join(' &nbsp; ')}</div>`;}

// ── TAB C — Caption Fingerprint ───────────────────────────────────────────────
async function renderFingerprintTab() {
  const el=document.getElementById('anal-tab-content'); if(!el) return;
  if(_fpChart){destroyChart(document.getElementById('fp-canvas'));_fpChart=null;}
  if(!_fpSeasons.length) _fpSeasons=[new Date().getFullYear()];

  el.innerHTML=`
    <div class="retro-prompt">CONFIGURE CAPTION FINGERPRINT PARAMETERS</div>
    <div style="display:flex;flex-wrap:wrap;gap:.75rem;align-items:flex-end;margin-bottom:.75rem">
      <div>
        <div class="retro-dim" style="font-size:.7rem;margin-bottom:.25rem;letter-spacing:.06em">BAND DESIGNATION</div>
        <select class="retro-select" id="fp-band-sel" onchange="window._fpBandChange()"><option value="">-- PICK A BAND --</option></select>
      </div>
      <div>
        <div class="retro-dim" style="font-size:.7rem;margin-bottom:.25rem;letter-spacing:.06em">SEASON(S)</div>
        <div id="fp-season-chips">${seasonChipsHtml(_fpSeasons,'window._fpSeasonToggle')}</div>
      </div>
    </div>
    <div id="fp-chart-area">
      <div class="retro-splash">
        <div class="retro-splash-title retro-glow">CAPTION FINGERPRINT MODULE</div>
        <div class="retro-dim">PERCENTILE RANK WITHIN COMPETITIVE FIELD</div>
        <div class="retro-dim">MULTI-SEASON RADAR · BOOK STATUS PER CAPTION</div>
        <div style="margin-top:.5rem">SELECT BAND TO INITIALIZE <span class="retro-blink">_</span></div>
      </div>
    </div>
  `;
  await populateBandSel('fp-band-sel',_fpBandId);
  if(_fpBandId){document.getElementById('fp-band-sel').value=_fpBandId;await loadFpData();}

  window._fpBandChange=async()=>{_fpBandId=parseInt(document.getElementById('fp-band-sel')?.value)||null;if(_fpBandId)await loadFpData();};
  window._fpSeasonToggle=y=>{if(_fpSeasons.includes(y)){if(_fpSeasons.length===1)return;_fpSeasons=_fpSeasons.filter(s=>s!==y);}else _fpSeasons=[..._fpSeasons,y].sort();document.getElementById('fp-season-chips').innerHTML=seasonChipsHtml(_fpSeasons,'window._fpSeasonToggle');if(_fpBandId)loadFpData();};
}

async function loadFpData(){const area=document.getElementById('fp-chart-area');if(!area||!_fpBandId)return;area.innerHTML=`<div class="retro-loading"><span class="retro-blink">█</span> LOADING FINGERPRINT DATA...</div>`;
  try{const data=await apiFetch(`/api/competition-analysis/caption-fingerprint/${_fpBandId}?seasons=${_fpSeasons.join(',')}`);renderFpArea(data);}catch(err){area.innerHTML=`<div class="retro-err" style="padding:1rem">ERR: ${err.message}</div>`;}}

function renderFpArea(data){const area=document.getElementById('fp-chart-area');if(!area)return;
  const {seasons}=data;const hasData=seasons.some(s=>Object.keys(s.percentiles).length>0);
  if(!hasData){area.innerHTML=`<div class="retro-dim" style="padding:1rem">INSUFFICIENT DATA — NEED 2+ BANDS AT SHARED EVENTS TO COMPUTE PERCENTILE RANK.</div>`;return;}
  const allGroups=[...new Set(seasons.flatMap(s=>Object.keys(s.percentiles)))].sort();
  const bookStatus=seasons[seasons.length-1].book_status||{};
  const bookHtml=Object.entries(bookStatus).map(([g,bs])=>{if(!bs)return'';const icons={ROOM_TO_GROW:'📈 ROOM',MAXED_OUT:'⬛ MAXED',OVERPLAYED:'⚠️ OVER'};const icon=icons[bs.status]||bs.status||'—';const col=bs.status==='ROOM_TO_GROW'?'#33ff33':bs.status==='OVERPLAYED'?'#ff4444':'#ffb000';return`<div class="retro-book-cell"><div class="bc-name">${g.toUpperCase()}</div><div class="bc-scores"><span title="Left box">${bs.sub1?.toFixed(1)||'—'}</span><span class="retro-dim"> / </span><span title="Right box">${bs.sub2?.toFixed(1)||'—'}</span>${bs.total!=null?`<span class="retro-dim"> → </span><span>${bs.total.toFixed(2)}</span>`:''}</div><div style="color:${col};font-size:.75rem">${icon}</div></div>`;}).join('');

  area.innerHTML=`<div class="retro-canvas-wrap radar"><canvas id="fp-canvas"></canvas></div><hr class="retro-divider"><div class="retro-dim" style="font-size:.7rem;letter-spacing:.06em;margin-bottom:.4rem">BOOK STATUS — LATEST SHOW</div>${bookHtml?`<div class="retro-book-grid">${bookHtml}</div>`:'<div class="retro-dim" style="font-size:.78rem">NO SUB-SCORE DATA AVAILABLE.</div>'}`;
  requestAnimationFrame(()=>{const c=document.getElementById('fp-canvas');if(!c)return;if(_fpChart){destroyChart(c);_fpChart=null;}_fpChart=makeRadarChart(c,allGroups,seasons);});}

// ── TAB D — Championship Forecast ────────────────────────────────────────────
async function renderForecastTab() {
  const el=document.getElementById('anal-tab-content'); if(!el) return;
  const cy=new Date().getFullYear();

  el.innerHTML=`
    <div class="retro-prompt">CONFIGURE CHAMPIONSHIP FORECAST PARAMETERS</div>
    <div style="display:flex;flex-wrap:wrap;gap:.75rem;align-items:flex-end;margin-bottom:.75rem">
      <div>
        <div class="retro-dim" style="font-size:.7rem;margin-bottom:.25rem;letter-spacing:.06em">BAND DESIGNATION</div>
        <select class="retro-select" id="fc-band-sel" onchange="window._fcBandChange()"><option value="">-- PICK A BAND --</option></select>
      </div>
      <div>
        <div class="retro-dim" style="font-size:.7rem;margin-bottom:.25rem;letter-spacing:.06em">SEASON</div>
        <select class="retro-select" id="fc-season-sel" onchange="window._fcSeasonChange()">
          ${[cy,cy-1,cy-2].map(y=>`<option value="${y}" ${y===_fcSeason?'selected':''}>${y}</option>`).join('')}
        </select>
      </div>
    </div>
    <div id="fc-content">
      <div class="retro-splash">
        <div class="retro-splash-title retro-glow">CHAMPIONSHIP FORECAST MODULE</div>
        <div class="retro-dim">PROJECTED SCORE RANGE · FORECAST HISTORY · DAVID'S READ</div>
        <div style="margin-top:.5rem">SELECT BAND TO INITIALIZE <span class="retro-blink">_</span></div>
      </div>
    </div>
  `;
  await populateBandSel('fc-band-sel',_fcBandId);
  if(_fcBandId){document.getElementById('fc-band-sel').value=_fcBandId;await loadFcData();}
  window._fcBandChange=async()=>{_fcBandId=parseInt(document.getElementById('fc-band-sel')?.value)||null;if(_fcBandId)await loadFcData();};
  window._fcSeasonChange=async()=>{_fcSeason=parseInt(document.getElementById('fc-season-sel')?.value)||new Date().getFullYear();if(_fcBandId)await loadFcData();};
}

async function loadFcData(){const el=document.getElementById('fc-content');if(!el||!_fcBandId)return;el.innerHTML=`<div class="retro-loading"><span class="retro-blink">█</span> LOADING FORECAST DATA...</div>`;
  try{const data=await apiFetch(`/api/competition-analysis/forecast-history/${_fcBandId}?season=${_fcSeason}`);renderFcArea(data);}catch(err){el.innerHTML=`<div class="retro-err" style="padding:1rem">ERR: ${err.message}</div>`;}}

function renderFcArea(data){const el=document.getElementById('fc-content');if(!el)return;
  const {band,season,history,field,latest_entry}=data;
  const latest=history?.length?history[history.length-1]:null;
  if(!latest){el.innerHTML=`<div class="retro-dim" style="padding:1rem">NO FORECAST DATA FOR ${band.name} ${season}. IMPORT RECAPS TO GENERATE.</div>`;return;}
  const conf=latest.confidence;
  const confColor={LOW:'#ff4444',MEDIUM:'#ffb000',HIGH:'#33ff33',VERY_HIGH:'#33ff33'}[conf]||'#888';
  const estShows=5,progress=Math.min(latest.generated_after_show/estShows,1);
  const projLow=parseFloat(latest.projected_low),projMid=parseFloat(latest.projected_mid),projHigh=parseFloat(latest.projected_high);
  const range=projHigh-projLow;

  // Forecast card
  const card=`<div class="retro-forecast-card">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:.75rem">
      <div>
        <div class="retro-dim" style="font-size:.7rem;letter-spacing:.06em;margin-bottom:.25rem">CHAMPIONSHIP PROJECTION — ${band.name} ${season}</div>
        <div style="font-size:1.4rem;font-variant-numeric:tabular-nums;letter-spacing:.04em" class="retro-glow">${projMid.toFixed(3)}</div>
        <div style="font-size:.8rem;color:#1a8c1a">${projLow.toFixed(3)} – ${projHigh.toFixed(3)}</div>
      </div>
      <div style="text-align:right">
        <div style="font-size:.7rem;color:#1a8c1a;letter-spacing:.06em">CONFIDENCE</div>
        <div style="font-size:1rem;color:${confColor};letter-spacing:.08em">${conf}</div>
        ${latest.cold_start?'<div class="retro-warn" style="font-size:.7rem">COLD START</div>':''}
      </div>
    </div>
    <div class="retro-dim" style="font-size:.7rem;letter-spacing:.06em;margin-bottom:.2rem">SEASON PROGRESS — SHOW ${latest.generated_after_show} OF ~${estShows}</div>
    <div class="retro-progress-bar"><div class="retro-progress-fill" style="width:${(progress*100).toFixed(0)}%"></div></div>
  </div>`;

  // History timeline
  const prevByShow={};
  for(const h of history) prevByShow[h.generated_after_show]=h;
  const timelineRows=history.map((h,i)=>{
    const prev=i>0?history[i-1]:null;
    const mid=parseFloat(h.projected_mid),prevMid=prev?parseFloat(prev.projected_mid):null;
    const delta=prevMid!=null?mid-prevMid:null;
    const col=delta==null?'#888':delta>0?'#33ff33':delta<0?'#ff4444':'#888';
    return`<div class="retro-timeline-row">
      <span class="tl-show">SHW ${h.generated_after_show}</span>
      <span class="tl-range">${parseFloat(h.projected_low).toFixed(3)} – ${parseFloat(h.projected_high).toFixed(3)}</span>
      <span class="tl-conf">[${h.confidence}]</span>
      ${delta!=null?`<span class="tl-delta" style="color:${col}">${delta>0?'+':''}${delta.toFixed(3)}</span>`:''}
    </div>`;
  }).join('');

  // Field placement table
  const fieldHtml=field?.length?`<table class="retro-field-table">
    <thead><tr><th>#</th><th>BAND</th><th>SCORE</th></tr></thead>
    <tbody>${field.map(r=>{const isP=r.band_name===band.name;return`<tr class="${isP?'is-primary':''}"><td>${r.placement??'—'}</td><td>${r.band_name}${isP?' ◄':''}</td><td>${r.total_score!=null?parseFloat(r.total_score).toFixed(3):'—'}</td></tr>`;}).join('')}</tbody>
  </table>`:'<div class="retro-dim" style="font-size:.78rem">NO FIELD DATA.</div>';

  // Narrative
  const narrativeHtml=latest.narrative
    ?`<div class="retro-narrative-box">${latest.narrative}</div>`
    :`<div class="retro-dim" style="font-size:.78rem;margin-bottom:.4rem">NARRATIVE NOT YET GENERATED.</div>
      <button class="retro-btn" id="gen-narrative-btn" onclick="window._genNarrative(${latest.id})">[ GENERATE DAVID'S READ ]</button>`;

  el.innerHTML=`${card}
    <hr class="retro-divider">
    <div class="retro-dim" style="font-size:.7rem;letter-spacing:.06em;margin-bottom:.3rem">FORECAST PROGRESSION</div>
    <div class="retro-timeline">${timelineRows}</div>
    <hr class="retro-divider">
    <div class="retro-dim" style="font-size:.7rem;letter-spacing:.06em;margin-bottom:.3rem">COMPETITIVE FIELD — LATEST SHOW</div>
    ${fieldHtml}
    <hr class="retro-divider">
    <div class="retro-dim" style="font-size:.7rem;letter-spacing:.06em;margin-bottom:.3rem">DAVID'S READ</div>
    ${narrativeHtml}
  `;

  window._genNarrative=async(forecastId)=>{
    const btn=document.getElementById('gen-narrative-btn');
    if(btn){btn.textContent='[ GENERATING... ]';btn.disabled=true;}
    try{
      const result=await apiFetch(`/api/competition-analysis/forecast/${_fcBandId}/narrative?season=${_fcSeason}`,{method:'POST'});
      const box=document.querySelector('.retro-narrative-box');
      if(box){box.textContent=result.narrative||'';}
      else{
        const wrap=btn?.parentElement;
        if(wrap){wrap.innerHTML=`<div class="retro-narrative-box">${result.narrative||'(no narrative)'}</div>`;}
      }
    }catch(err){if(btn){btn.textContent='[ RETRY GENERATE ]';btn.disabled=false;}alert('Failed: '+err.message);}
  };
}
